/**
 * View Component: FavoriteBar
 * Floating bottom bar showing current selection count and actions.
 * Appears only when at least 1 item is favorited.
 *
 * T-Cron-2026-10-22 20:00 (round-4-of-day) — 改进清单 #14「CSV/JSON 导出」
 * + #10「无障碍:aria-label 覆盖率」
 *
 * 修的问题:
 *   1. 「确认选材」按钮**是死的** —— 没有 onClick、没有 aria-label。
 *      用户点它没有任何反应,只能自己一条条复制文案。
 *      ⇒ 现在它打开一个导出面板(CSV / JSON 两选),导出的是**已选**的素材。
 *   2. 「清空」按钮没有 aria-label,读屏只会念"清空",不知道清的是谁。
 *   3. 整个浮条没有 role="region" + aria-label,读屏用户 Tab 到这里
 *      不知道这是一块什么。
 *   4. 浮条是 fixed 定位,Tab 时焦点会跑到它**后面**的页面上,
 *      视觉焦点和读屏焦点分离 ⇒ 加 focus-visible 焦点环 + 可聚焦。
 *
 * 导出范围:仅「已选」(favorites)。这是浮条的语义 —— 它就是选材确认栏。
 * 「导出当前可见」放在 Header 里更合适,下一轮做。
 *
 * Props:
 *   count: number
 *   onClear: () => void
 *   favoritePosts: Array  已选的 post(必须来自 store —— 收藏态在 store 里,
 *                      原始 POSTS 上没有 favorite 标记,直接读 window.POSTS
 *                      会永远导出 0 条)
 */
// NOTE: 不要显式 `import React from 'react'` —— vite.config.js 的
// esbuild.jsxInject 已经给每个 .jsx 注入 React,重复 import 会报
// "Identifier \"React\" has already been declared" 并让整轮构建失败。
// T-Cron-2026-10-24:触感反馈收口到 harness/haptics.js。
import { buzz as buzzHaptic } from '../harness/haptics.js';
// T-Cron-2026-10-31 08:00:toast 收口到 harness/DismissibleToast.jsx
// (点击 / Esc 关闭 + 键盘可达),4 个 toast 入口行为一致。
import { DismissibleToast } from '../harness/DismissibleToast.jsx';

function FavoriteBar({ count, onClear, favoritePosts }) {
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [toast, setToast] = React.useState('');
  const toastTimer = React.useRef(null);

  // T-Cron-2026-11-01 08:00 早间 — a11y #10:导出面板补齐模态语义。
  //
  // 🔴 这个面板和本轮修的「保存封面」面板是**同一类缺陷**:
  //   它已经声明了 role="dialog" + aria-modal="true",
  //   却没有焦点陷阱、没有 tabIndex、没有 Escape 关闭。
  //   ⇒ 键盘用户打开导出面板后 Tab 会跑到背后的看板卡片上,
  //     焦点跑丢;Escape 也关不掉(只能去够「取消」按钮或点遮罩)。
  //   ⇒ 声明了 aria-modal 却没有对应的模态行为 = **比不声明更糟**
  //     (读屏告诉用户"背景不可交互",但实际上背景可以拿到焦点)。
  //
  // 收口到 harness/useFocusTrap.js,与详情抽屉/筛选抽屉/保存面板同源。
  const exportSheetRef = React.useRef(null);
  useFocusTrap(exportSheetRef, { enabled: sheetOpen, autoFocus: true });

  // Escape 关闭导出面板。
  // 🔴 用 capture 阶段 + stopPropagation:本项目**全局**快捷键 handler
  //   (App.jsx 的 useKeyboardShortcuts)挂在 document 上,
  //   它也监听 Escape 类的按键;不拦住的话会同时触发全局动作。
  React.useEffect(() => {
    if (!sheetOpen) return undefined;
    const onSheetKey = (e) => {
      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setSheetOpen(false);
      }
    };
    document.addEventListener('keydown', onSheetKey, true);
    return () => document.removeEventListener('keydown', onSheetKey, true);
  }, [sheetOpen]);

  // 触感反馈:导出是"完成了一个动作",给一次短促的确认震动。
  // T-Cron-2026-10-24:收口到 harness/haptics.js —— 本文件原本内联了一份
  // prefers-reduced-motion 判断,全项目现在只有一处实现。
  const buzz = React.useCallback((ms = 12) => buzzHaptic(ms), []);

  const showToast = React.useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  }, []);

  // 组件卸载时清掉定时器,避免 setState 打在已卸载组件上。
  React.useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const handleExport = (format) => {
    setSheetOpen(false);
    const favs = favoritePosts || [];
    const result = window.Exporter.exportPosts(favs, format, 'favorites');
    if (result.ok) {
      buzz(12);
      showToast(result.message);
    } else {
      buzz(30);
      showToast(result.message || '导出失败,请重试');
    }
  };

  return (
    <>
      <div
        className={`fav-bar ${count > 0 ? 'visible' : ''}`}
        role="region"
        aria-label="已选素材操作栏"
      >
        <span className="fav-bar-count">
          已选 <span>{count}</span> 篇素材
        </span>
        <button
          className="fav-bar-action"
          onClick={() => setSheetOpen(true)}
          aria-label={`导出已选的 ${count} 篇素材`}
          aria-haspopup="dialog"
        >
          确认选材
        </button>
        <button
          className="fav-bar-clear"
          onClick={onClear}
          aria-label="清空已选素材"
        >
          清空
        </button>
      </div>

      {/* 导出面板 —— 复用 PostDetail 的 download-sheet 视觉规范(暖白/iOS 26) */}
      {sheetOpen && (
        <div className="download-sheet-overlay" onClick={() => setSheetOpen(false)}>
          <div
            ref={exportSheetRef}
            className="download-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="导出已选素材"
            tabIndex={-1}
          >
            <div className="download-sheet-title">导出已选素材（{count} 篇）</div>
            <div className="download-sheet-hint">
              导出 <b>{count}</b> 篇已选素材,CSV 可直接用 Excel / 飞书表格打开,
              JSON 保留完整文案与全部版本。
            </div>
            <div className="download-sheet-actions">
              <button
                className="download-sheet-btn primary"
                onClick={() => handleExport('csv')}
                aria-label="导出为 CSV 表格文件"
              >
                导出 CSV
              </button>
              <button
                className="download-sheet-btn"
                onClick={() => handleExport('json')}
                aria-label="导出为 JSON 文件"
              >
                导出 JSON
              </button>
              <button
                className="download-sheet-btn"
                onClick={() => setSheetOpen(false)}
                aria-label="取消导出"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      <DismissibleToast
        message={toast}
        onDismiss={() => {
          setToast('');
          if (toastTimer.current) clearTimeout(toastTimer.current);
        }}
      />
    </>
  );
}

window.FavoriteBar = FavoriteBar;

export default FavoriteBar;
